import React, { useEffect, useState } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap-css-only/css/bootstrap.min.css';
import 'mdbreact/dist/css/mdb.css';
import { getUsername, isUserAdmin, isUserLoggedIn } from './user/User';
import { Button, Row } from 'react-bootstrap';
import '../style/Admin.css';
import ActionableTeamsDataTable from '../modules/datatables/ActionableTeamsDataTable';
import AllMatchesDataTable from '../modules/datatables/AllMatchesDataTable';
import LeaguesDataTable from '../modules/datatables/LeaguesDataTable';
import SeasonsDataTable from '../modules/datatables/SeasonsDataTable';
import UsersDataTable from '../modules/datatables/UsersDataTable';
import PlayersDataTable from '../modules/datatables/PlayersDataTable';
import { SeasonPopup } from './SeasonPopup';
import { TeamPopup } from './TeamPopup';
import { PlayerPopup } from './PlayerPopup';
import {
  fetchAdminMatches,
  fetchTeams,
  fetchAdminPlayers,
  fetchAdminSeasons,
  fetchAdminLeagues,
  fetchUsers,
  fetchSeasons,
  postGenerateCalendar,
  postGenerateSecondRoundCalendar,
  postDisqualifyTeam,
  deleteUser,
} from '../util/api/requests';
import { getLeagueNamesFromSeasons, renderSpinner } from '../util/util';
import { LeaguePopup } from './LeaguePopup';
import { UserPopup } from './UserPopup';
import Col from 'react-bootstrap/Col';

const MATCH_ENTRIES = 25;
export const Admin = () => {
  const [matches, setMatches] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeSeasons, setActiveSeasons] = useState([]);
  const [disqSeasonId, setDisqSeasonId] = useState('');
  const [disqTeamId, setDisqTeamId] = useState('');
  const [disqLoading, setDisqLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const [isSeasonPopupVisible, setIsSeasonPopupVisible] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState();

  const [isLeaguePopupVisible, setIsLeaguePopupVisible] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState();

  const [isTeamPopupVisible, setIsTeamPopupVisible] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState();

  const [players, setPlayers] = useState([]);
  const [isPlayerPopupVisible, setIsPlayerPopupVisible] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState();

  const leagueNames = getLeagueNamesFromSeasons(seasons);

  const [isUserPopupVisible, setIsUserPopupVisible] = useState(false);

  const [loading, setLoading] = useState({
    matches: false,
    seasons: false,
    leagues: false,
    teams: false,
    users: false,
  });

  const loadMatches = async () => {
    setLoading((l) => ({ ...l, matches: true }));
    try {
      const adminMatches = await fetchAdminMatches();
      if (adminMatches) {
        setMatches(adminMatches);
      }
    } catch (error) {
      console.error('Failed to load admin matches:', error);
    } finally {
      setLoading((l) => ({ ...l, matches: false }));
    }
  };

  const loadTeams = async () => {
    setLoading((l) => ({ ...l, teams: true }));
    try {
      const teams = await fetchTeams();
      if (teams) {
        setTeams(teams);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading((l) => ({ ...l, teams: false }));
    }
  };

  const loadPlayers = async () => {
    try {
      const data = await fetchAdminPlayers();
      if (data) {
        setPlayers(data);
      }
    } catch (error) {
      console.error('Failed to load players:', error);
    }
  };

  const loadSeasons = async () => {
    setLoading((l) => ({ ...l, seasons: true }));
    try {
      const adminSeasons = await fetchAdminSeasons();
      if (adminSeasons) {
        setSeasons(adminSeasons);
      }
    } catch (error) {
      console.error('Failed to load seasons:', error);
    } finally {
      setLoading((l) => ({ ...l, seasons: false }));
    }
  };

  const loadLeagues = async () => {
    setLoading((l) => ({ ...l, leagues: true }));
    try {
      const adminLeagues = await fetchAdminLeagues();
      if (adminLeagues) {
        setLeagues(adminLeagues);
      }
    } catch (error) {
      console.error('Failed to load leagues:', error);
    } finally {
      setLoading((l) => ({ ...l, leagues: false }));
    }
  };

  const loadUsers = async () => {
    setLoading((l) => ({ ...l, users: true }));
    try {
      const users = await fetchUsers();
      if (users) {
        setUsers(users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading((l) => ({ ...l, users: false }));
    }
  };

  const loadActiveSeasons = async () => {
    try {
      const data = await fetchSeasons();
      if (data) setActiveSeasons(data);
    } catch (error) {
      console.error('Failed to load active seasons:', error);
    }
  };

  useEffect(() => {
    loadSeasons();
    loadTeams();
    loadPlayers();
    loadMatches();
    loadLeagues();
    loadUsers();
    loadActiveSeasons();
  }, []);

  const editSeason = (seasonId) => {
    setSelectedSeason(seasons.find((season) => season.id === seasonId));
    setIsSeasonPopupVisible(true);
  };

  const editLeague = (leagueId) => {
    setSelectedLeague(leagues.find((league) => league.id === leagueId));
    setIsLeaguePopupVisible(true);
  };

  const editTeam = (teamId) => {
    setSelectedTeam(teams.find((team) => team.id === teamId));
    setIsTeamPopupVisible(true);
  };

  const editPlayer = (playerId) => {
    setSelectedPlayer(players.find((p) => p.id === playerId));
    setIsPlayerPopupVisible(true);
  };

  const generateAction = async (leagueId) => {
    const data = { leagueId };
    try {
      await postGenerateCalendar(data);
      loadLeagues();
      loadMatches();
    } catch (error) {
      console.error('Failed to generate action:', error);
    }
  };

  const generateSecondRoundAction = async (leagueId) => {
    const data = { leagueId };
    try {
      await postGenerateSecondRoundCalendar(data);
      loadLeagues();
      loadMatches();
    } catch (error) {
      console.error('Failed to generate second round action:', error);
    }
  };

  const deleteUserAction = async (userId) => {
    try {
      await deleteUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleNewSeasonClick = () => {
    setSelectedSeason(undefined);
    setIsSeasonPopupVisible(true);
  };

  const handleNewTeamClick = () => {
    setSelectedTeam(undefined);
    setIsTeamPopupVisible(true);
  };

  const handleNewUserClick = () => {
    setIsUserPopupVisible(true);
  };

  const disqTeamOptions = () => {
    const season = activeSeasons.find((s) => String(s.id) === disqSeasonId);
    if (!season) return [];
    return (season.leagues || []).flatMap((league) =>
      (league.teams || []).map((team) => ({
        teamId: team.id,
        teamName: team.name,
        leagueId: league.id,
        leagueName: league.name,
      }))
    );
  };

  const handleDisqualify = async () => {
    const option = disqTeamOptions().find((o) => String(o.teamId) === disqTeamId);
    if (!option) return;
    try {
      setDisqLoading(true);
      await postDisqualifyTeam(option.leagueId, option.teamId);
      setDisqTeamId('');
      await loadActiveSeasons();
    } catch (error) {
      console.error('Disqualification failed:', error);
    }
    setDisqLoading(false);
  };

  const renderAdminContent = () => {
    return (
      <>
        <Row className={'justify-content-between'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Matšid</h3>
          </Col>
        </Row>

        {matches.length > 0 && (
          <AllMatchesDataTable
            allMatches={matches}
            paging={true}
            searching={true}
            sortable={false}
            entries={25}
          />
        )}
        {loading.matches && renderSpinner()}

        <Row className={'justify-content-between'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Hooajad</h3>
          </Col>
          <Col xs={12} lg={6} className="text-lg-right text-xs-center">
            <Button variant="primary" onClick={handleNewSeasonClick}>
              Lisa uus hooaeg
            </Button>
          </Col>
        </Row>
        {seasons.length > 0 && (
          <SeasonsDataTable
            seasons={seasons}
            paging={true}
            editAction={editSeason}
          />
        )}
        {loading.seasons && renderSpinner()}

        <Row className={'justify-content-between mt-5'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Liigad</h3>
          </Col>
        </Row>
        {leagues.length > 0 && (
          <LeaguesDataTable
            leagues={leagues}
            paging={true}
            generateAction={generateAction}
            generateSecondRoundAction={generateSecondRoundAction}
            editAction={editLeague}
          />
        )}
        {loading.leagues && renderSpinner()}

        <Row className={'justify-content-between mt-5'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Võistkonnad</h3>
          </Col>
          <Col xs={12} lg={6} className="text-lg-right text-xs-center">
            <Button variant="primary" onClick={handleNewTeamClick}>
              Lisa uus võistkond
            </Button>
          </Col>
        </Row>

        {teams.length > 0 && (
          <ActionableTeamsDataTable
            actionableTeams={teams}
            paging={true}
            editAction={editTeam}
          />
        )}
        {loading.teams && renderSpinner()}

        <Row className={'justify-content-between mt-5'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Kasutajad</h3>
          </Col>
          <Col xs={12} lg={6} className="text-lg-right text-xs-center">
            <Button variant="primary" onClick={handleNewUserClick}>
              Lisa uus kasutaja
            </Button>
          </Col>
        </Row>

        {users.length > 0 && (
          <UsersDataTable
            users={users}
            paging={true}
            deleteAction={deleteUserAction}
          />
        )}
        {loading.users && renderSpinner()}

        <Row className={'justify-content-between mt-5'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Mängijad</h3>
          </Col>
        </Row>
        <PlayersDataTable players={players} editAction={editPlayer} />

        <Row className={'justify-content-between mt-5'}>
          <Col xs={12} lg={6} className="text-lg-left text-xs-center">
            <h3>Diskvalifitseeri võistkond</h3>
          </Col>
        </Row>
        <Row className={'align-items-end mt-2'}>
          <Col xs={12} md={4} className="mb-2">
            <select
              className="form-control"
              value={disqSeasonId}
              onChange={(e) => { setDisqSeasonId(e.target.value); setDisqTeamId(''); }}
            >
              <option value=''>-- vali hooaeg --</option>
              {activeSeasons.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
          </Col>
          <Col xs={12} md={4} className="mb-2">
            <select
              className="form-control"
              value={disqTeamId}
              onChange={(e) => setDisqTeamId(e.target.value)}
              disabled={!disqSeasonId}
            >
              <option value=''>-- vali võistkond --</option>
              {disqTeamOptions().map((o) => (
                <option key={o.teamId} value={String(o.teamId)}>
                  {o.teamName} ({o.leagueName})
                </option>
              ))}
            </select>
          </Col>
          <Col xs={12} md={4} className="mb-2">
            <Button
              variant="danger"
              disabled={!disqTeamId || disqLoading}
              onClick={handleDisqualify}
            >
              {disqLoading ? 'Palun oota...' : 'Diskvalifitseeri'}
            </Button>
          </Col>
        </Row>

        <SeasonPopup
          isSeasonPopupVisible={isSeasonPopupVisible}
          setIsSeasonPopupVisible={setIsSeasonPopupVisible}
          selectedSeason={selectedSeason}
          leagueNames={leagueNames}
          loadSeasons={loadSeasons}
          loadLeagues={loadLeagues}
        />

        <LeaguePopup
          isLeaguePopupVisible={isLeaguePopupVisible}
          setIsLeaguePopupVisible={setIsLeaguePopupVisible}
          selectedLeague={selectedLeague}
          allTeams={teams.map((team) => ({
            id: team.id,
            name: team.name,
            players: team.players,
            seasonScore: null,
          }))}
          loadLeagues={loadLeagues}
        />

        <TeamPopup
          isTeamPopupVisible={isTeamPopupVisible}
          setIsTeamPopupVisible={setIsTeamPopupVisible}
          selectedTeam={selectedTeam}
          loadTeams={loadTeams}
        />

        <PlayerPopup
          isVisible={isPlayerPopupVisible}
          setIsVisible={setIsPlayerPopupVisible}
          player={selectedPlayer}
          onSaved={loadPlayers}
        />

        <UserPopup
          allTeams={teams}
          users={users}
          isUserPopupVisible={isUserPopupVisible}
          setIsUserPopupVisible={setIsUserPopupVisible}
          forceUsersUpdate={loadUsers}
        />
      </>
    );
  };

  const renderUserContent = () => {
    return (
      <>
        <h1 className={'m-5'}>Tere, {getUsername()}!</h1>
        <Row className={'justify-content-between'}>
          <h3 className="ml-3">Matšid</h3>
        </Row>

        {matches.length > 0 && (
          <AllMatchesDataTable
            allMatches={matches}
            paging={true}
            searching={true}
            sortable={false}
            entries={MATCH_ENTRIES}
          />
        )}
      </>
    );
  };

  return (
    <div className="admin-wrapper" style={{ minHeight: '80vh' }}>
      {isUserAdmin() && renderAdminContent()}
      {!isUserAdmin() && isUserLoggedIn && renderUserContent()}
    </div>
  );
};
